import { useUserDetailsQuery } from '@/__generated__/graphql';
import * as state from '@/state';
import { Alert, AlertTitle, Box, CircularProgress, Stack } from '@mui/material';
import { useAtom } from 'jotai';
import { Snackbar } from '../../shared/Snackbar';
import { RolesAndPermission } from './RolesAndPermissions';
import { UserDetails } from './UserDetails';
import { UserGroups } from './UserGroups';
import { UserPlatforms } from './UserPlatforms';
import { ProfileContainer } from './styles';
import { BackButton } from '../styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useRouter } from '@/hooks';

type Props = {
  id: string | undefined;
};

export const UserProfile = ({ id }: Props) => {
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const router = useRouter();

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const { shouldShow, content } = snackbarMessage;

  const { loading, data, error } = useUserDetailsQuery({
    variables: {
      userId: id,
    },
  });
  const userDetails = data?.userDetails || undefined;

  if (loading)
    return (
      <CircularProgress
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginTop: '-20px',
          marginLeft: '-20px',
        }}
      />
    );

  if (error || !userDetails) {
    const errors: string[] = error?.graphQLErrors?.map((error_) => error_?.message) ?? [];
    return (
      <Box sx={{ mt: 5 }}>
        {errors.map((error_, index) => (
          <Alert severity="error" key={index} sx={{ mb: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error_} — <BackButton>Go back</BackButton>
          </Alert>
        ))}
      </Box>
    );
  }

  return (
    <ProfileContainer>
      <BackButton onClick={() => router.back()}>
        <AbyssIcon iconName="long-back" />
        <span>BACK</span>
      </BackButton>
      <Stack sx={{ pb: 5 }} gap="2.4rem" width="100%">
        <UserDetails user={userDetails.user} />
        <UserGroups groups={userDetails.userGroups} />
        <RolesAndPermission user={userDetails.user} userId={id || ''} />
        <UserPlatforms structures={userDetails.structures} />
      </Stack>
      <Snackbar open={shouldShow} onClose={closeSnack}>
        {content}
      </Snackbar>
    </ProfileContainer>
  );
};
